import type { DefaultError } from "@tanstack/vue-query";
import { AxiosError, isAxiosError } from "axios";
import camelCase from "lodash-es/camelCase";

const isNotFoundError = (error: DefaultError) => {
  return Boolean(
    error.cause &&
      typeof error.cause === "object" &&
      "statusCode" in error.cause &&
      error.cause.statusCode === 404,
  );
};

const notFoundQueryOptions = {
  throwOnError: isNotFoundError,
  retry: (failureCount: number, error: DefaultError) => {
    // Don't retry 404s
    if (isNotFoundError(error)) {
      return false;
    }

    return failureCount < 2;
  },
};

type Failure = {
  errorCode: string;
  errorMessage: string;
  propertyName: string;
};

type Options = {
  /**
   * If true, will return the raw error message from the error object instead of only the error messages from the `failures` array.
   */
  allowRawErrorMessages?: boolean;
  allowReadableRawMessage?: boolean;
  fallbackErrorMessage?: string;
};

/**
 * Get error message from error object from the backend.
 * If the error object has a `failures` array, returns the `errorMessage` from each failure.
 * If the `failures` array is empty, returns a "Something went wrong" message.
 */
function getErrorMessage(
  error: unknown,
  options: Options = {
    allowRawErrorMessages: false,
    allowReadableRawMessage: false,
  },
) {
  const failureMessages: string[] = [];

  const axiosError = error as AxiosError;
  if (!axiosError) {
    return failureMessages;
  }

  const errorResponseData = axiosError?.response?.data as
    | { failures: Failure[] }
    | undefined;

  if (errorResponseData?.failures) {
    const failures = errorResponseData.failures;

    failures.forEach((failure) => {
      failureMessages.push(failure.errorMessage);
    });
  }

  if (options.allowRawErrorMessages) {
    failureMessages.push(axiosError.message);
  }

  if (options.allowReadableRawMessage) {
    failureMessages.push(axiosError.message.replace("AxiosError: ", ""));
  }

  if (failureMessages.length === 0) {
    failureMessages.push(
      options.fallbackErrorMessage || "Something went wrong",
    );
  }

  return failureMessages;
}

/**
 * Get error messages from error object from the backend, mapped on property name. Will return properties in camelCase
 * @param error
 * @returns {Record<string, string[]>>}
 */
export function getErrorsPerProperty(error: unknown): Record<string, string[]> {
  const failureMessages: Record<string, string[]> = {};

  if (!error || !isAxiosError(error) || !error.response) {
    return failureMessages;
  }

  const errorResponseData = error.response.data as
    | { failures: Failure[] }
    | undefined;

  if (errorResponseData?.failures) {
    const failures = errorResponseData.failures;

    failures.forEach((f) => {
      if (!f.propertyName) return;

      const propertyName = camelCase(f.propertyName);
      failureMessages[propertyName] = [
        ...(failureMessages[propertyName] ?? []),
        f.errorMessage,
      ];
    });
  }

  return failureMessages;
}

export { type Failure, notFoundQueryOptions, isNotFoundError, getErrorMessage };
